import {
  useQuery,
  UseQueryOptions,
  UseQueryResult,
  useMutation,
} from 'react-query';
import { useToast } from '@chakra-ui/react';
import { Pagination, User, GenericUser } from '~/types';
import {
  getUsers,
  getGenericUsers,
  getUserById,
  GetResponseUsers,
  updateUserById,
} from '~/services';
import { PAGE_SIZE } from '~/constants';
import { useAuth } from '~/hooks';
import { queryClient } from '~/utils/react-query';
import { getUsersByGroup } from '~/services/Users';

export function useUsers(
  page: number,
  search = '',
  pageSize: number = PAGE_SIZE,
  options: UseQueryOptions<GetResponseUsers, unknown> = {},
): UseQueryResult<GetResponseUsers, unknown> {
  return useQuery<GetResponseUsers, unknown>(
    ['users', page],
    () => getUsers(page, search, pageSize),
    {
      staleTime: 1000 * 60 * 10, // 10 minutes
      ...options,
    },
  );
}

export function useGenericUsers(
  page: number,
  search = '',
  pageSize: number = PAGE_SIZE,
  options: UseQueryOptions<Pagination<GenericUser>, unknown> = {},
): UseQueryResult<Pagination<GenericUser>, unknown> {
  return useQuery<Pagination<GenericUser>, unknown>(
    ['users', page, pageSize, search],
    () => getGenericUsers(page, search, pageSize),
    {
      staleTime: 1000 * 60 * 10, // 10 minutes
      ...options,
    },
  );
}

export function useUsersByGroup(
  idGroup: string,
  page: number,
  pageSize: number = PAGE_SIZE,
  options: UseQueryOptions<Pagination<GenericUser>, unknown> = {},
): UseQueryResult<Pagination<GenericUser>, unknown> {
  return useQuery<Pagination<GenericUser>, unknown>(
    ['usersByGroup', idGroup, page, pageSize],
    () => getUsersByGroup(idGroup, page, pageSize),
    {
      staleTime: 1000 * 60 * 10, // 10 minutes
      ...options,
    },
  );
}

export function useUser(
  id: number,
  type: string,
): UseQueryResult<User, unknown> {
  return useQuery<User, unknown>(['user', id], () => getUserById(id, type), {
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
}

export function useUpdateUser() {
  const toast = useToast();
  const { updateUser } = useAuth();

  return useMutation(
    async (data: { id: number; type: string; formData: any }) =>
      updateUserById(data.id, data.type, data.formData),
    {
      onSuccess: ({ data }) => {
        toast({
          title: 'Alterado com sucesso',
          description: 'Dados alterados com sucesso!',
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        updateUser(data);
        queryClient.invalidateQueries('users');
        queryClient.invalidateQueries('user');
        queryClient.invalidateQueries('usersByGroup');
      },
      onError: (err: any) => {
        if (err.response.status === 400) {
          toast({
            title: 'Erro',
            description: err.response.data.detail,
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        } else {
          toast({
            title: 'Erro',
            description: 'Erro ao atualizar dados, tente novamente',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
      },
    },
  );
}
